import { create } from 'zustand';

/**
 * Pre-generation estimate settings chosen on the working intro screen (no
 * estimate row exists yet, so there is nothing to PATCH). Only the fields the
 * user actually touched are stored — the effective value falls back to the
 * app locale at read time. Currency / measurement system are deliberately NOT
 * draftable: the generation derives them from the LLM's display
 * recommendation, and the title tags switch them on the ready estimate.
 */
export interface EstimateDraft {
  language?: string;
  special_requirements?: string;
}

interface EstimateDraftState {
  /** Per-concept drafts — in-memory only, deliberately not persisted. */
  drafts: Record<string, EstimateDraft>;
  patchDraft: (conceptId: string, patch: Partial<EstimateDraft>) => void;
  clearDraft: (conceptId: string) => void;
}

/**
 * In-memory store for the intro screen's draft generation settings, shared by
 * the stacked inline settings (EstimateTabContent) and the two-column
 * inspector panel (EstimateSettingsPanel) — separate React subtrees that must
 * read the same draft. Cleared once the POST succeeds (the created estimate
 * owns the values from then on).
 */
export const useEstimateDraftStore = create<EstimateDraftState>((set) => ({
  drafts: {},
  patchDraft: (conceptId, patch) =>
    set((state) => ({
      drafts: {
        ...state.drafts,
        [conceptId]: { ...state.drafts[conceptId], ...patch },
      },
    })),
  clearDraft: (conceptId) =>
    set((state) => {
      const { [conceptId]: _removed, ...rest } = state.drafts;
      return { drafts: rest };
    }),
}));
